import { AuthNgSchemaTypes } from "@bfc-software/api-schemas";
import { LocalStorage } from "@bfc-software/app-toolbox";
import type { AuthService } from "@bfc-software/app-toolbox";
import { QueryClient } from "@tanstack/react-query";
import createFetchClient, { Middleware } from "openapi-fetch";
import createClient from "openapi-react-query";
import { getEnv } from "@bfc-software/bfc-web-ui/environment";
import { logger } from "@bfc-software/app-toolbox";

const fetchClient = () => {
  const token = LocalStorage.getGlobalJwt();

  const client = createFetchClient<AuthNgSchemaTypes.paths>({
    baseUrl: getEnv().authServiceUrl,
    credentials: "include",
    headers: {
      "X-ACCESS-KEY": getEnv().authAccessKey,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  client.use({
    onRequest: ({ request }) => {
      const parsedUrl = new URL(request.url);
      logger.info(`Executing auth request to '${parsedUrl.pathname}'`);
      return request;
    },
  } as Middleware);

  return client;
};

export const apiClient = () => createClient(fetchClient());

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
    },
  },
});

/**
 * AuthService implementation using the AAA openapi-fetch client.
 * Passed to AuthContextProvider so the state machine can make API calls.
 */
export function createAuthService(): AuthService {
  const client = () => fetchClient();

  return {
    async redirect({ token, tokenType }) {
      const { data, error } = await client().GET("/auth/redirect", {
        params: { query: { token, stytch_token_type: tokenType } },
      });
      if (error) throw error;
      return data!;
    },

    async getOrgsForUser({ email }) {
      const { data, error } = await client().GET("/org/get-for-user", {
        params: { query: { email_addr: email } },
      });
      if (error) throw error;
      return data!;
    },

    async authenticateUser({ email, orgId, password }) {
      const { data, error } = await client().POST("/auth/user", {
        body: { email_addr: email, org_id: orgId, user_pwd: password },
      });
      if (error) throw error;
      return data!;
    },

    async exchange({ orgId, session }) {
      const { data, error } = await client().POST("/auth/exchange", {
        body: { org_id: orgId, session },
      });
      if (error) throw error;
      return data!;
    },

    /**
     * Uses a dedicated fetch client instead of the shared `client()` because
     * this endpoint requires the user JWT - not the AAA token that `client()`
     * reads from localStorage. The AAA token doesn't exist yet; it's the
     * value this call returns.
     */
    async createSession({ locationId, token }) {
      const userJwtClient = () =>
        createFetchClient<AuthNgSchemaTypes.paths>({
          baseUrl: getEnv().authServiceUrl,
          credentials: "include",
          headers: {
            "X-ACCESS-KEY": getEnv().authAccessKey,
            Authorization: `Bearer ${token}`,
          },
        });

      // No warehouse yet - the user is signed in but has not entered a product
      // that needs one.
      //
      // /authz/create-session cannot serve this: its schema marks warehouseId
      // required and the handler rejects an unknown one with 401.
      // /authz/exchange-token takes the same user JWT, treats warehouseId as
      // optional, and omits the warehouse claims when it is absent.
      //
      // Both return the signed AAA JWT, under different names - create-session
      // puts it in `tenantId`, exchange-token in `token`.
      if (locationId === null) {
        const { data, error } = await userJwtClient().POST(
          "/authz/exchange-token",
          { body: {} },
        );
        if (error) throw error;
        return { tenantId: data!.token, dakota_session: "" };
      }

      const { data, error } = await userJwtClient().POST(
        "/authz/create-session",
        { body: { warehouseId: locationId } },
      );
      if (error) throw error;
      return data!;
    },

    async refreshUser({ token }) {
      const { data, error } = await client().POST("/auth/user-refresh", {
        body: { token },
      });
      if (error) throw error;
      return data!;
    },

    async getPolicy() {
      const { data, error } = await client().GET("/authz/get-policy");
      if (error) throw error;
      return data!;
    },
  };
}
